import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import { ResizableImage } from './ResizebleImage'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'
import { CommApiInstance } from 'Utils/CommApiInstance'
import EditorToolbar from './EditorToolbar'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import { FontSize } from './FontSize'
import imageCompression from 'browser-image-compression'
import "../Assets/css/EditorToolbar.css"
import Youtube from '@tiptap/extension-youtube'

interface PresignedUrlBatchItem {
  presignedUrl: string
  imgUrl: string
  objectKey: string
}

interface PresignedUrlBatchResponse {
  apiHdr: {
    code: string
    msg: string
    rqstTrxId?: string
  }
  apiBody: {
    thmbUrl?: string
    brdImgUrlLst: PresignedUrlBatchItem[]
  }
}

interface PresignedUrlBatchResult {
  rqstTrxId: string
  thmbUrl: string
  brdImgUrlLst: PresignedUrlBatchItem[]
}

async function requestPresignedUrlsBatch(files: File[]): Promise<PresignedUrlBatchResult> {
  const { data } = await CommApiInstance.post<PresignedUrlBatchResponse>(
    '/brd/KRBBO/img/presigned_url',
    {
      apiBody: {
        brdImgLst: files.map((f) => ({
          flNm: f.name.trim().replace(/\s+/g, '_'),
          cntntType: f.type,
        })),
      },
    }
  )

  if (data?.apiHdr?.code !== 'MEMB0000') {
    throw new Error(data?.apiHdr?.msg || 'presigned url 발급 실패')
  }

  return {
    rqstTrxId: data?.apiHdr?.rqstTrxId || '',
    thmbUrl: data?.apiBody?.thmbUrl || '',
    brdImgUrlLst: data?.apiBody?.brdImgUrlLst || [],
  }
}

// 업로드는 안 하고 blob만 삽입 + pendingFiles에 File을 보관해둔다
function insertImagePlaceholder(
  editor: Editor,
  file: File,
  pendingFiles: Map<string, File>,
  pos?: number
): string {
  const blobUrl = URL.createObjectURL(file)
  pendingFiles.set(blobUrl, file)
  const { schema, tr, selection } = editor.state
  const node = schema.nodes.image.create({ src: blobUrl })
  const insertPos = pos ?? selection.from
  editor.view.dispatch(tr.insert(insertPos, node))
  return blobUrl
}

function replaceImageSrc(editor: Editor, oldSrc: string, newSrc: string) {
  const { state } = editor
  let targetPos: number | null = null
  let targetAttrs: Record<string, any> | null = null
  state.doc.descendants((node, pos) => {
    if (node.type.name === 'image' && node.attrs.src === oldSrc) {
      targetPos = pos
      targetAttrs = node.attrs
    }
  })
  if (targetPos !== null && targetAttrs) {
    editor.view.dispatch(
      editor.state.tr.setNodeMarkup(targetPos, undefined, {
        ...(targetAttrs as Record<string, any>),
        src: newSrc,
      })
    )
  }
}
const MAX_WIDTH_OR_HEIGHT = 1200
const MAX_SIZE_MB = 1
const SKIP_COMPRESSION_UNDER_BYTES = 500 * 1024

async function compressImageFile(file: File): Promise<File> {
  if (file.type === 'image/gif') return file
  if (file.size <= SKIP_COMPRESSION_UNDER_BYTES) return file

  try {
    const compressed = await imageCompression(file, {
      maxWidthOrHeight: MAX_WIDTH_OR_HEIGHT,
      maxSizeMB: MAX_SIZE_MB,
      initialQuality: 0.8,
      useWebWorker: true,
      fileType: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
    })
    return new File([compressed], file.name, { type: compressed.type })
  } catch (e) {
    console.error('이미지 압축 실패, 원본으로 업로드합니다', e)
    return file
  }
}

async function handleIncomingImageFile(
  editor: Editor,
  rawFile: File,
  pendingFiles: Map<string, File>,
  pos?: number
) {
  const file = await compressImageFile(rawFile)
  insertImagePlaceholder(editor, file, pendingFiles, pos)
}
export interface EditorUiHandle {
  /** 문서 안의 blob 이미지를 전부 업로드하고, 완료되면 최종 HTML을 반환한다 */
  uploadImagesAndGetHtml: () => Promise<string>
  getEditor: () => Editor | null
  getLastUploadMeta: () => { rqstTrxId: string; thmbUrl: string } | null
}

const EditorUi = forwardRef<EditorUiHandle>((_, ref) => {
  // blobUrl -> File 매핑. 등록 시점까지 여기 보관해둔다.
  const pendingFilesRef = useRef<Map<string, File>>(new Map())
  const lastUploadMetaRef = useRef<{ rqstTrxId: string; thmbUrl: string } | null>(null)

  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImage,
      Link.configure({ openOnClick: false, autolink: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({
        placeholder:
          '명예훼손, 허위정보, 비정상 플레이 유포 등 법률, 약관, 운영정책을 위반하는 게시글 등록 시 이용이 제한될 수 있습니다.\n게시글 작성 시 소중한 개인정보를 포함하지 않도록 주의 부탁드립니다.',
      }),
      TextStyle,
      Color,
      FontSize,
      Youtube.configure({
        width: 640,
        height: 360,
        nocookie: true,
      }),
    ],
    editorProps: {
      handleDrop: (view, event, slice, moved) => {
        const file = event.dataTransfer?.files?.[0]
        if (moved || !file?.type.startsWith('image/') || !editor) return false

        event.preventDefault()

        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY })
        const pos = coords?.pos ?? editor.state.selection.from

        handleIncomingImageFile(editor, file, pendingFilesRef.current, pos) 
        return true
      },
      handlePaste: (view, event) => {
        const file = event.clipboardData?.files?.[0]
        if (file?.type.startsWith('image/') && editor) {
          handleIncomingImageFile(editor, file, pendingFilesRef.current)
          return true
        }
        return false
      },
    },
  })

  const handleContainerDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const handleContainerDrop = (event: React.DragEvent<HTMLDivElement>) => {
    const alreadyHandled = event.defaultPrevented
    event.preventDefault()

    if (alreadyHandled || !editor) return

    const file = event.dataTransfer?.files?.[0]
    if (!file?.type.startsWith('image/')) return

    const coords = editor.view.posAtCoords({ left: event.clientX, top: event.clientY })
    const pos = coords?.pos ?? editor.state.selection.from
    handleIncomingImageFile(editor, file, pendingFilesRef.current, pos)
  }

  useImperativeHandle(ref, () => ({
    getEditor: () => editor,
    getLastUploadMeta: () => lastUploadMetaRef.current,
    uploadImagesAndGetHtml: async () => {
      if (!editor) return ''

      lastUploadMetaRef.current = null

      const pending = pendingFilesRef.current

      // 지금 문서에 실제로 남아있는 blob 이미지만 추린다
      const blobUrlsInDoc: string[] = []
      editor.state.doc.descendants((node) => {
        if (node.type.name === 'image' && node.attrs.src?.startsWith('blob:')) {
          blobUrlsInDoc.push(node.attrs.src)
        }
      })

      const targets = blobUrlsInDoc
        .map((blobUrl) => ({ blobUrl, file: pending.get(blobUrl) }))
        .filter((t): t is { blobUrl: string; file: File } => !!t.file)

      if (targets.length > 0) {
        const presignedResult = await requestPresignedUrlsBatch(targets.map((t) => t.file))
        const presignedItems = presignedResult.brdImgUrlLst
        lastUploadMetaRef.current = {
          rqstTrxId: presignedResult.rqstTrxId,
          thmbUrl: presignedResult.thmbUrl,
        }

        if (presignedItems.length < targets.length) {
          throw new Error('presigned url 개수가 업로드 대상보다 부족합니다')
        }

        await Promise.all(
          targets.map(async (t, i) => {
            const { presignedUrl, imgUrl } = presignedItems[i]
            const res = await fetch(presignedUrl, {
              method: 'PUT',
              headers: { 'Content-Type': t.file.type },
              body: t.file,
            })
            if (!res.ok) throw new Error(`${t.file.name} 업로드 실패`)

            replaceImageSrc(editor, t.blobUrl, imgUrl.trim())
            pending.delete(t.blobUrl)
            URL.revokeObjectURL(t.blobUrl)
          })
        )
      }

      return editor.getHTML()
    },
  }))

  return (
    <div className="write-editor">
      <EditorToolbar
        editor={editor}
        onImageUpload={(ed, file) => {
          handleIncomingImageFile(ed, file, pendingFilesRef.current)
        }}
      />
      <div
        className="editor-content-wrapper"
        onDragOver={handleContainerDragOver}
        onDrop={handleContainerDrop}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  )
})

EditorUi.displayName = 'EditorUi'

export default EditorUi